import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import { useLoadingBar } from 'react-top-loading-bar';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import DetailView from '../location/DetailView';
import { vendorsService } from '../../services/vendors.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base, borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", minHeight: 34, height: 34,
    fontSize: 13, boxShadow: "none", "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const VendorList = () => {
  const { start, complete } = useLoadingBar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [vendorRes, setVendorRes] = useState({ pagination: {}, vendors: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = { search: "", country: "", state: "", city: "", status: "" };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewVendor, setViewVendor] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    loadVendors();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadVendors = async () => {
    try {
      setLoading(true);
      start();
      const limit = Number(pagingLimit);
      const res = await vendorsService.list({
        page: Number(pageValue),
        limit,
        ...appliedFilters,
      });
      if (res?.success) {
        setVendorRes({
          pagination: res.resData?.pagination || {},
          vendors: res.resData?.vendors || []
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false)
      complete();
    }
  }

  const handleStatusChange = async (vendor, newStatus) => {
    if (newStatus === vendor?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark ${vendor?.company_name} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await vendorsService.updateStatus(vendor?.id, { status: newStatus });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadVendors();
    }
  }

  const handleDeleteVendor = async (vendor) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete vendor?",
      text: `Are you sure you want to delete ${vendor?.company_name}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await vendorsService.delete(vendor?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadVendors();
    }
  }

  const handleEditVendor = (vendor) => {
    navigate("/vendor/add", { state: { vendorId: vendor?.id } });
  }

  const handleViewVendor = async (vendorId) => {
    setShow(true);
    setViewVendor(null);
    try {
      setViewLoading(true);
      const res = await vendorsService.details(vendorId);
      if (res?.success) {
        setViewVendor(res.resData?.vendor ?? res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  }

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const vendors = vendorRes?.vendors || [];
  const totalRecords = vendorRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = vendors.filter(v => v?.status === "active").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const csvHeaders = [
    { label: "Vendor Code", key: "vendor_code" },
    { label: "Company Name", key: "company_name" },
    { label: "Executive", key: "executive" },
    { label: "Mobile", key: "mobile" },
    { label: "Email", key: "email" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    borderRadius: 10, background: bg, border: `1px solid ${border}`, color, fontSize: 13
  });

  const filterInput = { borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", fontSize: 13, height: 34 };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Vendors</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Vendors</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Vendor List</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/vendor/add" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
              <i className="bi bi-plus-lg"></i> Add Vendor
            </Link>
            <CSVLink
              data={vendors}
              headers={csvHeaders}
              filename="vendors.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input type="text" className="form-control" name="search" placeholder="Search code, company, executive, mobile, email" value={filters.search} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="country" placeholder="Country" value={filters.country} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="state" placeholder="State" value={filters.state} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="city" placeholder="City" value={filters.city} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Vendors</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{activeCount}</b> Active on this page</span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{vendors.length - activeCount}</b> Inactive on this page</span>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)', display: 'flex',
                justifyContent: 'center', alignItems: 'center', zIndex: 10
              }}>
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Vendor", "Executive", "Contact", "Location", "Status", "Actions"].map((h, i) => (
                      <th key={i} style={{
                        fontSize: "11px", fontWeight: "700", color: "#475569",
                        textTransform: "uppercase", letterSpacing: "0.6px",
                        borderBottom: "2px solid #e8edf5", padding: "10px 12px",
                        whiteSpace: "nowrap", background: "#f8f9fe"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((vendor, sno_i) => (
                    <tr key={vendor?.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 36, height: 36, background: '#e8f0fe', flexShrink: 0 }}>
                            <i className="bi bi-shop text-primary" style={{ fontSize: 14 }}></i>
                          </div>
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: 13 }}>{vendor?.company_name}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{vendor?.vendor_code}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="fw-medium" style={{ fontSize: 13 }}>{vendor?.executive}</span></td>
                      <td>
                        <div style={{ fontSize: 13 }}><i className="bi bi-telephone-fill me-1 text-primary" style={{ fontSize: 10 }}></i>{vendor?.mobile}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{vendor?.email}</div>
                      </td>
                      <td>
                        <div className="fw-medium" style={{ fontSize: 13 }}>{vendor?.city}{vendor?.state ? `, ${vendor.state}` : ""}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          <i className="bi bi-geo-alt-fill me-1 text-danger" style={{ fontSize: 10 }}></i>
                          {vendor?.country}{vendor?.pincode ? ` - ${vendor.pincode}` : ""}
                        </div>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={vendor?.status}
                          onChange={(e) => handleStatusChange(vendor, e.target.value)}
                          style={{
                            width: 'auto', minWidth: 0, height: 28, padding: '0 26px 0 10px', backgroundPosition: 'right 8px center', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            backgroundColor: vendor?.status === "active" ? '#d4edda' : '#f8d7da',
                            color: vendor?.status === "active" ? '#155724' : '#721c24',
                            border: 'none'
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" onClick={() => handleViewVendor(vendor?.id)} title="View"
                            style={{ width: 32, height: 32, borderRadius: 8, background: '#e6f7f1', color: '#0f8a5f', border: 'none', padding: 0 }}>
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" onClick={() => handleEditVendor(vendor)} title="Edit"
                            style={{ width: 32, height: 32, borderRadius: 8, background: '#e8f0fe', color: 'var(--btn-bg)', border: 'none', padding: 0 }}>
                            <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" onClick={() => handleDeleteVendor(vendor)} title="Delete"
                            style={{ width: 32, height: 32, borderRadius: 8, background: '#fce8e6', color: '#d93025', border: 'none', padding: 0 }}>
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && vendors.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-muted py-4" style={{ fontSize: 13 }}>No vendors found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
            <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> vendors</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
              </select>
              <span>Go</span>
              <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
              <div className="d-flex align-items-center gap-1">
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                {getPageNumbers().map((n, i) => n === "..."
                  ? <span key={"e" + i} className="px-1">...</span>
                  : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header closeButton closeVariant="white" style={{ background: 'linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)', borderBottom: 'none' }}>
            <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Vendor Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: '#f8f9fe' }}>
            {viewLoading || !viewVendor ? <ContentLoader loaderType="circleSpin" /> : (
              <DetailView fields={[
                { label: "Vendor Code", value: viewVendor.vendor_code },
                { label: "Company Name", value: viewVendor.company_name },
                { label: "Executive", value: viewVendor.executive },
                { label: "Mobile", value: viewVendor.mobile },
                { label: "Email", value: viewVendor.email },
                { label: "Address", value: viewVendor.address, full: true },
                { label: "Pincode", value: viewVendor.pincode },
                { label: "City", value: viewVendor.city },
                { label: "State", value: viewVendor.state },
                { label: "Country", value: viewVendor.country },
                { label: "Status", value: viewVendor.status, badge: true },
              ]} />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  )
}

export default VendorList;
